import { useState, useCallback } from 'react';
import { message } from 'antd';
import { getApiService, PaymentRequest } from '../../../shared/services/api/api.service';

export interface PaymentResult {
  transactionId: string;
  account: string;
  amount: number;
  phone?: string;
  promo?: string;
}

export interface PaymentError {
  code: number;
  transactionId: string;
  messages: string[];
  messageCodes: string[];
  account: string;
  amount: number;
  phone?: string;
  promo?: string;
}

export const usePayment = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPayment = useCallback(
    async (onayId: string, amount?: number): Promise<{ success: true; data: PaymentResult } | { success: false; error: PaymentError }> => {
      setIsLoading(true);
      setError(null);

      try {
        // Track start time for minimum display duration
        const startTime = Date.now();
        const MIN_LOADING_TIME = 3000; // 3 seconds

        // Generate transaction ID (UUID v4)
        const transactionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });

        const paymentData: PaymentRequest = {
          transaction_id: transactionId,
          account: onayId,
          provider: 'onay',
          ...(amount !== undefined && { amount }), // Only include amount if provided
        };

        console.log('Creating payment:', paymentData);

        const response = await getApiService().createPayment(paymentData);

        console.log('Payment response:', response);

        // Calculate remaining time to ensure minimum display duration
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

        // Wait for remaining time if needed
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }

        if (response.code === 200) {
          const result: PaymentResult = {
            transactionId: response.transaction_id,
            account: response.data?.account || '',
            amount: response.data?.amount || 0,
            phone: response.data?.phone,
            promo: response.promo,
          };
          return { success: true, data: result };
        } else {
          // Generate user-friendly messages from message codes if messages are not provided
          const messages = response.message || response.message_code.map(code => {
            // Map common error codes to Russian messages
            const errorMessages: Record<string, string> = {
              'ERR_ACCOUNT_DAILY_LIMIT_EXCEEDED': 'Превышен дневной лимит платежей для этого аккаунта',
            };
            return errorMessages[code] || code;
          });

          const errorData: PaymentError = {
            code: response.code,
            transactionId: response.transaction_id,
            messages: messages,
            messageCodes: response.message_code,
            account: response.data?.account || '',
            amount: response.data?.amount || 0,
            phone: response.data?.phone,
            promo: response.promo,
          };
          return { success: false, error: errorData };
        }
      } catch (err: any) {
        console.error('Payment error:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to create payment';
        setError(errorMessage);
        message.error(errorMessage);

        // Return a generic error for network/system failures
        const genericError: PaymentError = {
          code: 500,
          transactionId: 'N/A',
          messages: [errorMessage],
          messageCodes: ['NETWORK_ERROR'],
          account: onayId,
          amount: amount || 0,
        };
        return { success: false, error: genericError };
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return { createPayment, isLoading, error };
};
